import React, {
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Collapse, Card, CardTitle, CardSubtitle } from 'reactstrap';
import LucideIcon from '../lucide-icon';
import Info from '../info';
import Spinner from '../spinner';
import { SearchQueryContext } from '../filtro/FiltroDrawerNew';
import {
  findLabelsInChildren,
  filterChildren,
  extractHeaderText,
  normalizeText,
} from '../filtro/searchUtils';

function CardCustom({
  open: openProp = true,
  header,
  children,
  subtitle,
  headerStyle,
  errors = null,
  hideShadow = false,
  loadChildrenOnOpen = false,
  style,
  styleTitle,
  collapsible = true,
  collapseCallback,
  onCollapse,
  noPadding = false,
  ehAccordionDrawer = false,
  headerActions = [],
  collapseWithoutHeader = false,
  className,
  textInfoTitle,
  contentMarginTop = 28,
}) {
  const [open, setOpen] = useState(openProp);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const previousOpenProp = useRef(openProp);
  const previousSearchQuery = useRef(null);
  const pendingCollapseCallback = useRef(false);
  const searchContext = useContext(SearchQueryContext);
  const searchQuery = searchContext?.searchQuery || null;
  const searchIntl = searchContext?.intl || null;
  const hasSearchQuery = Boolean(searchQuery && searchQuery.trim());
  const hasMatch =
    hasSearchQuery && findLabelsInChildren(children, searchQuery, searchIntl);
  const headerMatches =
    hasSearchQuery &&
    Boolean(header) &&
    normalizeText(extractHeaderText(header)).includes(
      normalizeText(searchQuery)
    );

  useLayoutEffect(() => {
    if (openProp !== previousOpenProp.current) {
      previousOpenProp.current = openProp;
      setOpen(openProp);
    }
  }, [openProp]);

  useEffect(() => {
    if (searchQuery === previousSearchQuery.current) {
      return;
    }

    previousSearchQuery.current = searchQuery;
    if (hasMatch || headerMatches) {
      setOpen(true);
    }
  }, [searchQuery, hasMatch, headerMatches]);

  useLayoutEffect(() => {
    if (pendingCollapseCallback.current) {
      pendingCollapseCallback.current = false;
      collapseCallback?.(open);
    }
  }, [open, childrenLoaded, collapseCallback]);

  const handleCollapse = () => {
    if (!collapsible) {
      return;
    }

    onCollapse?.();
    pendingCollapseCallback.current = true;
    setOpen((previousOpen) => !previousOpen);
    setChildrenLoaded(true);
  };

  const isOpen = collapsible ? open : true;

  if (hasSearchQuery && !hasMatch && !headerMatches) {
    return null;
  }

  const visibleChildren = hasMatch
    ? filterChildren(children, searchQuery, searchIntl) || children
    : children;

  return (
    <Card
      body
      style={{
        marginBottom: 15,
        cursor: 'pointer',
        borderColor: errors ? 'red' : null,
        boxShadow: hideShadow ? 'none' : '',
        ...(ehAccordionDrawer
          ? {
              background: 'transparent',
              border: 'none',
              boxShadow: 'none',
              padding: 0,
              marginTop: 20,
              marginBottom: 0,
            }
          : {}),
        ...style,
      }}
      className={`${className} ${
        ehAccordionDrawer || noPadding ? '' : 'px-4 py-3'
      }`}
    >
      {(header || (collapsible && collapseWithoutHeader)) && (
        <>
          <CardTitle
            onClick={collapsible ? handleCollapse : undefined}
            className={headerStyle}
            style={{
              ...(ehAccordionDrawer
                ? {
                    color: 'var(--primary)',
                    fontWeight: 700,
                    fontSize: '16px',
                    cursor: collapsible ? 'pointer' : 'default',
                    paddingBottom: 6,
                  }
                : {}),
              ...(styleTitle || {}),
              ...(!collapsible ? { cursor: 'default' } : {}),
            }}
          >
            {header}
            {textInfoTitle && (
              <Info
                className="ml-1"
                id={`info-title-panel-${header}`}
                placement="right"
                text={textInfoTitle}
              />
            )}
            {headerActions}
            {collapsible && (
              <LucideIcon
                icon={isOpen ? 'ChevronUp' : 'ChevronDown'}
                size={ehAccordionDrawer ? 20 : 16}
                color={ehAccordionDrawer ? 'var(--primary)' : undefined}
                className={`pull-right${
                  headerActions ? ' ml-auto flex-shrink-0' : ''
                }`}
              />
            )}
          </CardTitle>
          {subtitle && (
            <CardSubtitle className="mt-1 text-muted">{subtitle}</CardSubtitle>
          )}
        </>
      )}
      <Collapse
        isOpen={isOpen}
        style={{
          marginTop: !ehAccordionDrawer && isOpen ? contentMarginTop : 0,
          transition: '0.3s ease-out',
          cursor: 'default',
        }}
      >
        <Suspense
          fallback={
            <div className="d-flex justify-content-center py-3">
              <Spinner size={32} />
            </div>
          }
        >
          {!loadChildrenOnOpen || isOpen || childrenLoaded
            ? visibleChildren
            : null}
        </Suspense>
      </Collapse>
    </Card>
  );
}

export default CardCustom;
